import {
  Button,
  Chevron,
  Edit,
  EmptyState,
  NoDataIcon,
  Spinner,
  Table,
  Tag
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useAbilityAccess } from 'components/AccessControl'
import dayjs from 'dayjs'
import { useState } from 'react'
import styled from 'styled-components'
import { useAlertsList } from '../hooks/useAlertsList'
import {
  formatDate,
  formatDateTime,
  getStatusLabel,
  getStatusTagColor
} from '../utils/alertUtils'
import type { Alert } from '../utils/types'

type AlertListProps = {
  residentId: string
  onEditAlert?: (alert: Alert) => void
}

const STATUS_SORT_ORDER: Record<string, number> = {
  active: 0,
  resolved: 1,
  enteredInError: 2
}

const AlertList = ({ residentId, onEditAlert }: AlertListProps) => {
  const { alerts, isLoading } = useAlertsList({ residentId })
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const canEditAlert = useAbilityAccess([{ i: 'update', this: 'Alert' }])

  const toggleExpand = (id: string) => {
    setExpandedKeys((prev) =>
      prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]
    )
  }

  if (isLoading) {
    return <Spinner style={{ minHeight: 200 }} />
  }

  if (!alerts || alerts.length === 0) {
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title="No alerts"
        description="No alerts have been recorded for this resident."
      />
    )
  }

  const columns = [
    {
      title: 'Added',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: '15%',
      sorter: (a: Alert, b: Alert) =>
        dayjs(a.createdOn).valueOf() - dayjs(b.createdOn).valueOf(),
      render: (value: string) => formatDate(value)
    },
    {
      title: 'Alert',
      key: 'alert',
      width: '40%',
      sorter: (a: Alert, b: Alert) => a.label.localeCompare(b.label),
      render: (_: unknown, record: Alert) => (
        <div>
          <AlertLabel>{record.label}</AlertLabel>
          {record.description && (
            <AlertDescription>{record.description}</AlertDescription>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: '25%',
      defaultSortOrder: 'ascend' as const,
      sorter: (a: Alert, b: Alert) =>
        (STATUS_SORT_ORDER[a.status] ?? 99) -
        (STATUS_SORT_ORDER[b.status] ?? 99),
      render: (_: unknown, record: Alert) => (
        <Tag color={getStatusTagColor(record.status)}>
          {getStatusLabel(record.status)}
        </Tag>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 1,
      render: (_: unknown, record: Alert) => (
        <StyledActionsCell>
          {record.isEditable && (
            <StyledEditButton
              variant="default"
              disabledByPermission={!canEditAlert}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                onEditAlert?.(record)
              }}
            >
              <Edit />
              Edit
            </StyledEditButton>
          )}
          {(record.statusHistory?.length ?? 0) > 0 && (
            <StyledChevronButton
              variant="default"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                toggleExpand(record.id)
              }}
            >
              View history
              <Chevron
                direction={expandedKeys.includes(record.id) ? 'up' : 'down'}
              />
            </StyledChevronButton>
          )}
        </StyledActionsCell>
      )
    }
  ]

  const dataSource = (alerts ?? []).map((alert) => ({
    ...alert,
    key: alert.id
  }))

  return (
    <StyledTableContainer>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        expandable={{
          expandedRowKeys: expandedKeys,
          expandedRowRender: (record: Alert) => (
            <StatusHistoryContent alert={record} />
          ),
          expandIcon: () => null,
          rowExpandable: (record: Alert) =>
            (record.statusHistory?.length ?? 0) > 0,
          showExpandColumn: false
        }}
      />
    </StyledTableContainer>
  )
}

type StatusHistoryContentProps = { alert: Alert }

const StatusHistoryContent = ({ alert }: StatusHistoryContentProps) => {
  if (!alert.statusHistory || alert.statusHistory.length === 0) {
    return null
  }

  return (
    <StatusHistoryList>
      <StatusHistoryTitle weight="bold">Status history</StatusHistoryTitle>
      {[...alert.statusHistory].reverse().map((entry) => (
        <StatusHistoryEntry key={`${entry.changedOn}-${entry.status}`}>
          <StatusHistoryRow>
            <strong>{getStatusLabel(entry.status)}</strong>
            {entry.changedBy?.name && <span> — {entry.changedBy.name}</span>}
            <DateText>{formatDateTime(entry.changedOn)}</DateText>
          </StatusHistoryRow>
          {entry.note && <NoteText>{entry.note}</NoteText>}
        </StatusHistoryEntry>
      ))}
    </StatusHistoryList>
  )
}

export default AlertList

const StyledTableContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  overflow: auto;
  padding-bottom: 40px;

  .ant-tag {
    border-radius: 4px;
  }

  .ant-table-column-sorters {
    flex-direction: row-reverse;
    gap: 8px;
  }

  .ant-table-thead > tr > th {
    color: ${({ theme }) => theme.color.neutral.color4} !important;
    font-weight: 500 !important;
    padding-bottom: 8px !important;
    padding-top: 8px !important;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background: ${({ theme }) => theme.color.neutral.color11};
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: ${({ theme }) => theme.color.neutral.color10};
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.color.neutral.color3} !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    vertical-align: top !important;
  }

  .ant-table-expanded-row > td {
    background: ${({ theme }) => theme.color.neutral.color10} !important;
  }
`

const StyledActionsCell = styled.div`
  align-items: stretch;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  white-space: nowrap;
`

const StyledEditButton = styled(Button)`
  gap: 2px;
  margin-right: 8px;
  padding: 6px 15px;
`

const StyledChevronButton = styled(Button)`
  min-height: 24px;
  padding: 6px 15px;
`

const AlertLabel = styled.div`
  font-weight: 600;
`

const AlertDescription = styled.div`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-size: 13px !important;
  margin-top: 2px;
`

const StatusHistoryTitle = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  display: block;
`

const StatusHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
`

const StatusHistoryEntry = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color?.neutral?.color8 || '#f0f0f0'};
  padding: 8px 0;

  &:last-child {
    border-bottom: none;
  }
`

const StatusHistoryRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const DateText = styled.span`
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#8c8c8c'};
  font-size: 12px;
  margin-left: auto;
`

const NoteText = styled.p`
  color: ${({ theme }) => theme.color?.neutral?.color5 || '#8c8c8c'};
  font-size: 13px;
  margin: 4px 0 0;
`
